import React from 'react';
import {
  Grid,
  FormControlLabel,
  Checkbox,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Autocomplete,
  Button,
  Divider,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Box,
  IconButton,
} from '@mui/material';
import {
  LocationOn as LocationOnIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useCardLocations } from '../../hooks/useCardLocations';
import AuthGuard from '../common/AuthGuard';

// Generic ownership data interface that works for both Pokemon and One Piece cards
export interface CardOwnershipData {
  owned: boolean;
  quantity: number;
  condition?: string;
  purchasePrice?: number;
  purchaseDate?: string;
  notes?: string;
  location?: string; // e.g., "Binder A", "Box 1", "Display Case"
  language?: string; // e.g., "EN" or "JP"
  disposition?: 'sold' | 'traded' | 'gifted' | null; // Card disposition status
  salePrice?: number; // Sale price (when disposition = 'sold')
  saleDate?: string; // Sale date (when disposition = 'sold')
  tradedFor?: string; // What the card was traded for (when disposition = 'traded')
  gradingCompany?: string;
  gradingGrade?: string;
  gradingId?: string;
  isGrading?: boolean;
  inCollection?: boolean;
  links?: string[];
}

interface CardOwnershipFormProps {
  initialData?: CardOwnershipData;
  onToggleOwnership?: () => void;
  onSave?: (data: CardOwnershipData) => Promise<void> | void;
  onCancel?: () => void;
  isSaving?: boolean;
}

const CardOwnershipForm: React.FC<CardOwnershipFormProps> = ({
  initialData,
  onToggleOwnership,
  onSave,
  onCancel,
  isSaving = false,
}) => {
  // Internal state
  const [formData, setFormData] = React.useState<CardOwnershipData>({
    owned: false,
    quantity: 1,
    condition: 'Near Mint',
    purchasePrice: undefined,
    purchaseDate: '',
    notes: '',
    location: '',
    language: 'EN',
    ...initialData
  });

  const [showAdvanced, setShowAdvanced] = React.useState(
    !!(initialData?.condition || initialData?.location || initialData?.gradingCompany || initialData?.isGrading || initialData?.purchasePrice || initialData?.disposition || (initialData?.links && initialData?.links.length > 0))
  );

  const [newLink, setNewLink] = React.useState('');

  const handleAddLink = () => {
    if (!newLink.trim()) return;
    let url = newLink.trim();
    if (!/^https?:\/\//i.test(url)) {
      url = 'https://' + url;
    }
    const currentLinks = formData.links || [];
    if (!currentLinks.includes(url)) {
      handleFieldChange('links', [...currentLinks, url]);
    }
    setNewLink('');
  };

  const handleRemoveLink = (index: number) => {
    const currentLinks = formData.links || [];
    const updated = currentLinks.filter((_, i) => i !== index);
    handleFieldChange('links', updated);
  };

  // Sync with initialData when it changes (e.g. data loaded)
  React.useEffect(() => {
    if (initialData) {
      setFormData(prev => ({ ...prev, ...initialData }));
    }
  }, [initialData]);
  
  // Fetch user's saved locations for autocomplete
  const { data: savedLocations = [], isLoading: locationsLoading } = useCardLocations();
  
  const handleFieldChange = (field: keyof CardOwnershipData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };
  
  const handleToggle = () => {
    setFormData(prev => ({ ...prev, owned: !prev.owned }));
    if (onToggleOwnership) {
        onToggleOwnership();
    }
  };
  
  const handleSaveClick = async () => {
      if (onSave) {
          // If showAdvanced is true, mark it as a collection entry
          const dataToSave = {
              ...formData,
              inCollection: showAdvanced
          };
          await onSave(dataToSave);
      }
  };

  return (
    <>
      <FormControlLabel
        control={
          <AuthGuard>
            <Checkbox
              checked={formData.owned}
              onChange={handleToggle}
            />
          </AuthGuard>
        }
        label="I own this card (Added to Master Set)"
      />

      {formData.owned && (
        <Grid container spacing={2}>
          {/* ... existing fields ... */}
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Quantity"
              type="number"
              value={formData.quantity}
              onChange={(e) => handleFieldChange('quantity', Number(e.target.value))}
              InputProps={{ inputProps: { min: 1 } }}
              helperText="Minimal info for basic tracking"
            />
          </Grid>

          <Grid item xs={6}>
            <FormControl fullWidth>
              <Typography variant="caption" sx={{ mb: 0.5, display: 'block', fontWeight: 'bold', color: 'text.secondary' }}>
                Card Language
              </Typography>
              <ToggleButtonGroup
                value={formData.language || 'EN'}
                exclusive
                onChange={(_, val) => val && handleFieldChange('language', val)}
                size="small"
                color="primary"
                fullWidth
                sx={{
                  height: '40px',
                  background: 'rgba(255, 255, 255, 0.03)',
                  backdropFilter: 'blur(4px)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  '& .MuiToggleButton-root': {
                    color: 'rgba(255, 255, 255, 0.5)',
                    border: 'none',
                    fontWeight: 600,
                    textTransform: 'none',
                    fontSize: '0.8rem',
                    flexGrow: 1,
                    '&.Mui-selected': {
                      color: '#fff',
                      background: 'linear-gradient(135deg, #1b3a60 0%, #1e4d8c 100%)',
                      boxShadow: '0 2px 8px rgba(27, 58, 96, 0.4)',
                    },
                    '&:hover': {
                      background: 'rgba(255, 255, 255, 0.08)',
                    }
                  }
                }}
              >
                <ToggleButton value="EN">English</ToggleButton>
                <ToggleButton value="JP">Japanese</ToggleButton>
              </ToggleButtonGroup>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Button 
                variant="outlined" 
                color="secondary"
                onClick={() => setShowAdvanced(!showAdvanced)}
                fullWidth
                sx={{ mb: 1 }}
            >
                {showAdvanced ? 'Hide Collection Details' : 'Add Collection Details (Condition, Price, Grading...)'}
            </Button>
          </Grid>

          {showAdvanced && (
            <>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" color="primary" sx={{ mb: 2 }}>
                    Collection Metadata
                </Typography>
              </Grid>
              <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel>Condition</InputLabel>
              <Select
                value={formData.condition}
                label="Condition"
                onChange={(e) => handleFieldChange('condition', e.target.value)}
              >
                <MenuItem value="Mint">Mint</MenuItem>
                <MenuItem value="Near Mint">Near Mint</MenuItem>
                <MenuItem value="Lightly Played">Lightly Played</MenuItem>
                <MenuItem value="Moderately Played">Moderately Played</MenuItem>
                <MenuItem value="Heavily Played">Heavily Played</MenuItem>
                <MenuItem value="Damaged">Damaged</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Grading Section */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.isGrading || false}
                  onChange={(e) => handleFieldChange('isGrading', e.target.checked)}
                />
              }
              label="This card is currently at grading"
            />
          </Grid>

          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel>Grading Company</InputLabel>
              <Select
                value={formData.gradingCompany || ''}
                label="Grading Company"
                onChange={(e) => handleFieldChange('gradingCompany', e.target.value)}
              >
                <MenuItem value="">Ungraded</MenuItem>
                <MenuItem value="PSA">PSA</MenuItem>
                <MenuItem value="CGC">CGC</MenuItem>
                <MenuItem value="BGS">BGS (Beckett)</MenuItem>
                <MenuItem value="TAG">TAG</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {formData.gradingCompany && (
            <>
              <Grid item xs={3}>
                <TextField
                  fullWidth
                  label="Grade"
                  placeholder="e.g. 10"
                  value={formData.gradingGrade || ''}
                  onChange={(e) => handleFieldChange('gradingGrade', e.target.value)}
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  fullWidth
                  label="Cert #"
                  placeholder="Certification ID"
                  value={formData.gradingId || ''}
                  onChange={(e) => handleFieldChange('gradingId', e.target.value)}
                />
              </Grid>
            </>
          )}

          <Grid item xs={12}>
            <Autocomplete
              freeSolo
              options={savedLocations}
              value={formData.location || ''}
              onChange={(event, newValue) => {
                handleFieldChange('location', newValue || '');
              }}
              onInputChange={(event, newInputValue) => {
                handleFieldChange('location', newInputValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Storage Location"
                  placeholder="e.g., Binder A, Box 1, Display Case"
                  helperText="Where do you store this card?"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <LocationOnIcon color="action" />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Purchase Price"
              type="number"
              value={formData.purchasePrice || ''}
              onChange={(e) =>
                handleFieldChange(
                  'purchasePrice',
                  e.target.value ? Number(e.target.value) : undefined
                )
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Purchase Date"
              type="date"
              value={formData.purchaseDate}
              onChange={(e) => handleFieldChange('purchaseDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* Seller Listing Links */}
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
              Seller Listing Links
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', mb: 2, color: 'text.secondary' }}>
              Add links to your online store listings (eBay, TCGPlayer, Shopify, etc.).
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                label="Listing URL"
                placeholder="https://www.ebay.com/itm/..."
                value={newLink}
                onChange={(e) => setNewLink(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddLink();
                  }
                }}
              />
              <Button variant="contained" onClick={handleAddLink} size="small">
                Add
              </Button>
            </Box>

            {formData.links && formData.links.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {formData.links.map((link, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 1,
                      borderRadius: '8px',
                      background: 'rgba(255, 255, 255, 0.03)',
                      border: '1px solid rgba(255, 255, 255, 0.08)',
                    }}
                  >
                    <Typography
                      variant="body2"
                      noWrap
                      sx={{ maxWidth: '85%', color: 'text.primary' }}
                    >
                      {link}
                    </Typography>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemoveLink(idx)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No active links listed.
              </Typography>
            )}
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Notes"
              value={formData.notes}
              onChange={(e) => handleFieldChange('notes', e.target.value)}
              multiline
              minRows={3}
              placeholder="Additional notes about this card..."
            />
          </Grid>

          {/* Disposition Status */}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Disposition</InputLabel>
              <Select
                value={formData.disposition || ''}
                label="Disposition"
                onChange={(e) => handleFieldChange('disposition', e.target.value || null)}
              >
                <MenuItem value="">None (Still Owned)</MenuItem>
                <MenuItem value="sold">Sold</MenuItem>
                <MenuItem value="traded">Traded</MenuItem>
                <MenuItem value="gifted">Gifted</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Conditional Fields for Sold */}
          {formData.disposition === 'sold' && (
            <>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Sale Price"
                  type="number"
                  value={formData.salePrice || ''}
                  onChange={(e) => handleFieldChange('salePrice', e.target.value ? parseFloat(e.target.value) : undefined)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Sale Date"
                  type="date"
                  value={formData.saleDate || ''}
                  onChange={(e) => handleFieldChange('saleDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </>
          )}

          {/* Conditional Field for Traded */}
          {formData.disposition === 'traded' && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Traded For"
                placeholder="e.g., Luffy Alt Art, $50 cash, etc."
                value={formData.tradedFor || ''}
                onChange={(e) => handleFieldChange('tradedFor', e.target.value)}
                helperText="Describe what you received in the trade"
              />
            </Grid>
          )}

            </>
          )}

          {/* Save/Cancel Buttons */}
          {onSave && (
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleSaveClick}
                disabled={isSaving}
                fullWidth
                sx={{ mb: 1 }}
              >
                {isSaving ? 'Saving...' : (showAdvanced ? 'Save to Detailed Collection' : 'Quick Save Ownership')}
              </Button>
              {onCancel && (
                <Button
                  size="large"
                  onClick={onCancel}
                  fullWidth
                  color="inherit"
                >
                  Cancel
                </Button>
              )}
            </Grid>
          )}
        </Grid>
      )}
    </>
  );
};

export default CardOwnershipForm;
